/**
 * Daily Reveal Per-Sport Configuration
 *
 * Single source of truth for how Daily Reveal scores, schedules, and optionally
 * runs prop-based gameplay per sport.
 */

import { type SportCode } from './leagues';
import { NFL_DAILY_REVEAL_CONFIG } from './nfl-daily-reveal';

export type DailyRevealMode = 'sport_default' | 'stat_hit' | 'prop_based';
export type ResolvedDailyRevealMode = Exclude<DailyRevealMode, 'sport_default'>;
export type DailyRevealPropOperator = 'over' | 'over_or_equal' | 'over_or_true';
export type DailyRevealPropMarketSource =
  | 'sportsbook_feed'
  | 'manual'
  | 'mock'
  | 'historical_replay'
  | 'csv_import';

export interface DailyRevealWeekDayTemplate {
  dayNumber: number;
  categoryKey?: string;
  categoryName?: string;
  gameWindow?: string;
}

export interface DailyRevealWeekConfig {
  type: 'sequential' | 'nfl_tuesday_to_monday';
  timezone?: string;
  days?: DailyRevealWeekDayTemplate[];
}

export interface DailyRevealPropCategoryConfig {
  categoryKey: string;
  categoryName: string;
  propStatType: string;
  operator: DailyRevealPropOperator;
  gameWindow?: string;
  eligiblePositions?: string[];
  lockRule?: string;
}

export interface DailyRevealPropBasedSportConfig {
  categories: DailyRevealPropCategoryConfig[];
  supportedSources: DailyRevealPropMarketSource[];
  defaultSource: DailyRevealPropMarketSource;
  defaultPackSize: number;
  allowPickChangeBeforeLock: boolean;
}

export interface DailyRevealSportConfig {
  /**
   * Deprecated alias retained for backwards compatibility with existing code.
   * Prefer `defaultMode`.
   */
  mode?: ResolvedDailyRevealMode;
  defaultMode?: ResolvedDailyRevealMode;
  weekConfig?: DailyRevealWeekConfig;
  propBasedConfig?: DailyRevealPropBasedSportConfig;
  hitStatType: string;
  defaultHitThreshold: number;
  /** Short unit label for the per-day hit, e.g. "HIT" (MLB), "PT" (NBA), "TD" (NFL). */
  hitStatLabel: string;
  ratingStatType: string;
  ratingLabel: string;
  ratingFormat: 'integer' | 'decimal1' | 'decimal3';
}

const DEFAULT_PROP_SOURCES: DailyRevealPropMarketSource[] = [
  'sportsbook_feed',
  'manual',
  'mock',
  'historical_replay',
];

const MLB_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = [
  { categoryKey: 'hit_king', categoryName: 'Hit King', propStatType: 'hitting_h', operator: 'over_or_equal' },
  { categoryKey: 'run_king', categoryName: 'Run King', propStatType: 'hitting_runs', operator: 'over_or_equal' },
  { categoryKey: 'rbi_king', categoryName: 'RBI King', propStatType: 'hitting_rbi', operator: 'over_or_equal' },
  { categoryKey: 'home_run_king', categoryName: 'Home Run King', propStatType: 'hitting_hr', operator: 'over_or_true' },
  { categoryKey: 'walk_king', categoryName: 'Walk King', propStatType: 'hitting_bb', operator: 'over_or_equal' },
  { categoryKey: 'steal_king', categoryName: 'Steal King', propStatType: 'hitting_sb', operator: 'over_or_true' },
  {
    categoryKey: 'pitcher_k_king',
    categoryName: 'Pitcher K King',
    propStatType: 'pitching_k',
    operator: 'over',
  },
];

const NBA_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = [
  { categoryKey: 'points_king', categoryName: 'Points King', propStatType: 'points', operator: 'over' },
  { categoryKey: 'threes_king', categoryName: 'Three King', propStatType: 'three_points_made', operator: 'over' },
  { categoryKey: 'rebound_king', categoryName: 'Rebound King', propStatType: 'rebounds', operator: 'over' },
  { categoryKey: 'assist_king', categoryName: 'Assist King', propStatType: 'assists', operator: 'over' },
  { categoryKey: 'steal_king', categoryName: 'Steal King', propStatType: 'steals', operator: 'over_or_equal' },
  { categoryKey: 'block_king', categoryName: 'Block King', propStatType: 'blocks', operator: 'over_or_equal' },
];

const SOCCER_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = [
  { categoryKey: 'goal_king', categoryName: 'Goal King', propStatType: 'soccer_goals', operator: 'over_or_true' },
  { categoryKey: 'assist_king', categoryName: 'Assist King', propStatType: 'soccer_assists', operator: 'over_or_equal' },
  {
    categoryKey: 'shots_on_target_king',
    categoryName: 'Shots On Target King',
    propStatType: 'soccer_shots_on_target',
    operator: 'over',
  },
  { categoryKey: 'shots_king', categoryName: 'Shots King', propStatType: 'soccer_shots', operator: 'over' },
];

const NFL_PROP_CATEGORIES: DailyRevealPropCategoryConfig[] = NFL_DAILY_REVEAL_CONFIG.days.map(day => ({
  categoryKey: day.categoryKey,
  categoryName: day.categoryName,
  propStatType: day.propStatType,
  operator: day.operator,
  gameWindow: day.gameWindow,
  lockRule: day.closeRule,
}));

export const DAILY_REVEAL_SPORT_CONFIG: Partial<Record<SportCode, DailyRevealSportConfig>> = {
  MLB: {
    defaultMode: 'stat_hit',
    weekConfig: { type: 'sequential' },
    propBasedConfig: {
      categories: MLB_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'sportsbook_feed',
      defaultPackSize: 3,
      allowPickChangeBeforeLock: true,
    },
    hitStatType: 'hitting_h',
    defaultHitThreshold: 1,
    hitStatLabel: 'HIT',
    ratingStatType: 'batting_average',
    ratingLabel: 'AVG',
    ratingFormat: 'decimal3',
  },
  NBA: {
    defaultMode: 'stat_hit',
    weekConfig: { type: 'sequential' },
    propBasedConfig: {
      categories: NBA_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'mock',
      defaultPackSize: 3,
      allowPickChangeBeforeLock: true,
    },
    hitStatType: 'points',
    defaultHitThreshold: 1,
    hitStatLabel: 'PT',
    ratingStatType: 'points_per_game',
    ratingLabel: 'PPG',
    ratingFormat: 'decimal1',
  },
  NCAAMB: {
    defaultMode: 'stat_hit',
    weekConfig: { type: 'sequential' },
    propBasedConfig: {
      categories: NBA_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'mock',
      defaultPackSize: 3,
      allowPickChangeBeforeLock: true,
    },
    hitStatType: 'points',
    defaultHitThreshold: 1,
    hitStatLabel: 'PT',
    ratingStatType: 'points_per_game',
    ratingLabel: 'PPG',
    ratingFormat: 'decimal1',
  },
  NFL: {
    // sport_default must preserve legacy NFL Daily Reveal (stat-hit / TD kings).
    // Prop-based Gridiron Kings contests opt in explicitly via daily_reveal_mode = 'prop_based'.
    defaultMode: 'stat_hit',
    weekConfig: {
      type: 'nfl_tuesday_to_monday',
      timezone: NFL_DAILY_REVEAL_CONFIG.timezone,
      days: NFL_DAILY_REVEAL_CONFIG.days.map(day => ({
        dayNumber: day.dayNumber,
        categoryKey: day.categoryKey,
        categoryName: day.categoryName,
        gameWindow: day.gameWindow,
      })),
    },
    propBasedConfig: {
      categories: NFL_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'sportsbook_feed',
      defaultPackSize: NFL_DAILY_REVEAL_CONFIG.packSize,
      allowPickChangeBeforeLock: NFL_DAILY_REVEAL_CONFIG.allowPickChangeBeforeLock,
    },
    hitStatType: 'total_touchdowns',
    defaultHitThreshold: 1,
    hitStatLabel: 'TD',
    ratingStatType: 'season_touchdowns',
    ratingLabel: 'TDs',
    ratingFormat: 'integer',
  },
  SOCCER: {
    defaultMode: 'stat_hit',
    weekConfig: { type: 'sequential' },
    propBasedConfig: {
      categories: SOCCER_PROP_CATEGORIES,
      supportedSources: DEFAULT_PROP_SOURCES,
      defaultSource: 'mock',
      defaultPackSize: 3,
      allowPickChangeBeforeLock: true,
    },
    hitStatType: 'soccer_goals',
    defaultHitThreshold: 1,
    hitStatLabel: 'GOAL',
    ratingStatType: 'season_goals',
    ratingLabel: 'Goals',
    ratingFormat: 'integer',
  },
};

export function getDailyRevealConfig(sport: string | null | undefined): DailyRevealSportConfig | null {
  if (!sport) return null;
  return DAILY_REVEAL_SPORT_CONFIG[sport.toUpperCase() as SportCode] ?? null;
}

export function isDailyRevealSupportedSport(sport: string | null | undefined): boolean {
  return getDailyRevealConfig(sport) !== null;
}

export function getDefaultDailyRevealModeForSport(
  sport: string | null | undefined
): ResolvedDailyRevealMode | null {
  const config = getDailyRevealConfig(sport);
  if (!config) return null;
  return config.defaultMode ?? config.mode ?? 'stat_hit';
}

/**
 * Deprecated alias for sport-level defaults. Prefer `resolveDailyRevealMode`.
 */
export function getDailyRevealMode(sport: string | null | undefined): ResolvedDailyRevealMode | null {
  return getDefaultDailyRevealModeForSport(sport);
}

export function resolveDailyRevealMode(
  sport: string | null | undefined,
  configuredMode?: DailyRevealMode | string | null
): ResolvedDailyRevealMode {
  if (configuredMode === 'stat_hit' || configuredMode === 'prop_based') {
    return configuredMode;
  }
  return getDefaultDailyRevealModeForSport(sport) ?? 'stat_hit';
}

export function isPropBasedDailyReveal(
  sport: string | null | undefined,
  configuredMode?: DailyRevealMode | null
): boolean {
  return resolveDailyRevealMode(sport, configuredMode) === 'prop_based';
}

export function getDailyRevealWeekConfig(sport: string | null | undefined): DailyRevealWeekConfig | null {
  return getDailyRevealConfig(sport)?.weekConfig ?? null;
}

export function getDailyRevealPropBasedConfig(
  sport: string | null | undefined
): DailyRevealPropBasedSportConfig | null {
  return getDailyRevealConfig(sport)?.propBasedConfig ?? null;
}

export function getDailyRevealPropCategoryByKey(
  sport: string | null | undefined,
  categoryKey: string | null | undefined
): DailyRevealPropCategoryConfig | null {
  if (!categoryKey) return null;
  const propConfig = getDailyRevealPropBasedConfig(sport);
  return propConfig?.categories.find(category => category.categoryKey === categoryKey) ?? null;
}

export function getDefaultDailyRevealPropCategoryForDay(
  sport: string | null | undefined,
  dayNumber: number
): DailyRevealPropCategoryConfig | null {
  const config = getDailyRevealConfig(sport);
  const propConfig = config?.propBasedConfig;
  if (!config || !propConfig || propConfig.categories.length === 0) return null;

  const configuredCategoryKey = config.weekConfig?.days?.find(day => day.dayNumber === dayNumber)?.categoryKey;
  if (configuredCategoryKey) {
    return getDailyRevealPropCategoryByKey(sport, configuredCategoryKey);
  }

  return propConfig.categories[(Math.max(dayNumber, 1) - 1) % propConfig.categories.length] ?? null;
}
